/** Pure helpers for materials price-update diffs (no Firebase). */

export interface MaterialsPriceNoOpDiff {
  action?: string;
  piecePrice?: number | null;
  costPerBdFt?: number | null;
  existingPiecePrice?: number | null;
  existingCostPerBdFt?: number | null;
  /** Hardware/fastener unit price (catalogDomain=hardware). */
  unitPrice?: number | null;
  existingUnitPrice?: number | null;
  /** Estimate → real mill/supplier flip is never a no-op even when prices match. */
  flipPrimarySupplier?: boolean;
}

function pricesWithinCent(a?: number | null, b?: number | null): boolean {
  if (a == null || b == null || !Number.isFinite(a) || !Number.isFinite(b)) return false;
  return Math.abs(a - b) <= 0.01 + 1e-9;
}

/** Catalog match whose displayed prices already match the quote (no supplier flip). */
export function isNoOpMaterialsPriceUpdate(diff: MaterialsPriceNoOpDiff): boolean {
  if (diff.action !== 'update') return false;
  if (diff.flipPrimarySupplier) return false;
  // Hardware diffs use unitPrice (no piece/bf).
  if (diff.unitPrice != null || diff.existingUnitPrice != null) {
    return pricesWithinCent(diff.unitPrice, diff.existingUnitPrice);
  }
  return (
    pricesWithinCent(diff.piecePrice, diff.existingPiecePrice)
    && pricesWithinCent(diff.costPerBdFt, diff.existingCostPerBdFt)
  );
}

export function visibleMaterialsPriceUpdates<T extends MaterialsPriceNoOpDiff>(
  updates: T[] | undefined,
): T[] {
  return (updates || []).filter(u => !isNoOpMaterialsPriceUpdate(u));
}

/** Hidden same-price matches: stored count (new proposals) or leftover update rows (legacy). */
export function omittedUnchangedMaterialsPriceCount(proposal: {
  updates?: MaterialsPriceNoOpDiff[];
  omittedUnchangedCount?: number;
} | null | undefined): number {
  if (!proposal) return 0;
  const hiddenInUpdates = (proposal.updates || []).length
    - visibleMaterialsPriceUpdates(proposal.updates).length;
  const stored = proposal.omittedUnchangedCount || 0;
  return Math.max(hiddenInUpdates, stored);
}
